import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { submitLead } from "../lib/api";
import useReveal from "../hooks/useReveal";

const INITIAL = {
    name: "",
    email: "",
    company: "",
    role: "",
    phone: "",
    interest: "early_access",
    message: "",
};

export default function EarlyAccess() {
    const [form, setForm] = useState(INITIAL);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [ref, visible] = useReveal();

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Name and email are required");
            return;
        }
        try {
            setSubmitting(true);
            await submitLead(form);
            setSubmitted(true);
            toast.success("You&apos;re on the list. We&apos;ll be in touch.");
            setForm(INITIAL);
        } catch (err) {
            const detail = err?.response?.data?.detail || "Could not submit. Try again.";
            toast.error(typeof detail === "string" ? detail : "Submission failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="early-access"
            data-testid="early-access-section"
            className="relative py-20 sm:py-28 lg:py-36 border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div ref={ref} className={`reveal ${visible ? "is-visible" : ""} grid lg:grid-cols-12 gap-10`}>
                    <div className="lg:col-span-5">
                        <span className="section-tag" data-testid="ea-tag">Early Access · 04</span>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-5 leading-tight">
                            Get on the list. Shape the product.
                        </h2>
                        <p className="text-zinc-400 mt-5 leading-relaxed">
                            We&apos;re building WE DELVR LOGISTICS with Australian importers &amp; exporters,
                            not in isolation. Register interest to influence the roadmap and
                            get first access when we open the platform.
                        </p>
                        <ul className="mt-8 space-y-3">
                            {[
                                "Priority onboarding to the product platform",
                                "Free freight cost diagnostic for early members",
                                "Quarterly insights on AU trade lanes & carriers",
                                "No spam. Opt out any time.",
                            ].map((b) => (
                                <li key={b} className="flex items-start gap-3 text-zinc-300">
                                    <CheckCircle2 size={18} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm">{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="card-glow rounded-2xl p-6 sm:p-8 lg:p-10">
                            {submitted ? (
                                <div className="text-center py-10" data-testid="ea-success">
                                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-cyan-400/40 bg-cyan-400/10 mb-5">
                                        <CheckCircle2 size={28} className="text-cyan-300" />
                                    </div>
                                    <h3 className="font-display text-2xl text-white">You&apos;re on the list</h3>
                                    <p className="text-zinc-400 mt-2 max-w-md mx-auto">
                                        We&apos;ll reach out personally with next steps and your early-access diagnostic.
                                    </p>
                                    <button
                                        onClick={() => setSubmitted(false)}
                                        className="btn-secondary mt-7 mx-auto"
                                        data-testid="ea-reset-btn"
                                    >
                                        Submit another
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4" data-testid="ea-form">
                                    <Field label="Full name *" testid="ea-input-name">
                                        <input
                                            type="text"
                                            value={form.name}
                                            onChange={update("name")}
                                            className="input-dark w-full rounded-lg px-4 py-3 text-sm"
                                            placeholder="Jane Citizen"
                                            data-testid="ea-input-name-field"
                                            required
                                        />
                                    </Field>
                                    <Field label="Work email *" testid="ea-input-email">
                                        <input
                                            type="email"
                                            value={form.email}
                                            onChange={update("email")}
                                            className="input-dark w-full rounded-lg px-4 py-3 text-sm"
                                            placeholder="jane@company.com.au"
                                            data-testid="ea-input-email-field"
                                            required
                                        />
                                    </Field>
                                    <Field label="Company" testid="ea-input-company">
                                        <input
                                            type="text"
                                            value={form.company}
                                            onChange={update("company")}
                                            className="input-dark w-full rounded-lg px-4 py-3 text-sm"
                                            placeholder="Your business"
                                            data-testid="ea-input-company-field"
                                        />
                                    </Field>
                                    <Field label="Role" testid="ea-input-role">
                                        <input
                                            type="text"
                                            value={form.role}
                                            onChange={update("role")}
                                            className="input-dark w-full rounded-lg px-4 py-3 text-sm"
                                            placeholder="Head of Supply Chain"
                                            data-testid="ea-input-role-field"
                                        />
                                    </Field>
                                    <Field label="I am a..." testid="ea-input-interest" full>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { v: "importer", l: "Importer" },
                                                { v: "exporter", l: "Exporter" },
                                                { v: "both", l: "Both" },
                                            ].map((opt) => (
                                                <button
                                                    type="button"
                                                    key={opt.v}
                                                    onClick={() => setForm((f) => ({ ...f, interest: opt.v }))}
                                                    data-testid={`ea-interest-${opt.v}`}
                                                    className={`rounded-lg px-3 py-3 text-sm border transition-all ${
                                                        form.interest === opt.v
                                                            ? "border-cyan-400 text-cyan-300 bg-cyan-400/10"
                                                            : "border-white/10 text-zinc-400 hover:border-white/25"
                                                    }`}
                                                >
                                                    {opt.l}
                                                </button>
                                            ))}
                                        </div>
                                    </Field>
                                    <Field label="What's hurting your supply chain today?" testid="ea-input-message" full>
                                        <textarea
                                            rows={4}
                                            value={form.message}
                                            onChange={update("message")}
                                            className="input-dark w-full rounded-lg px-4 py-3 text-sm resize-none"
                                            placeholder="Tell us about your biggest freight or visibility pain point..."
                                            data-testid="ea-input-message-field"
                                        />
                                    </Field>
                                    <div className="sm:col-span-2 flex items-center justify-between gap-3 mt-2 flex-wrap">
                                        <p className="text-[11px] font-mono-accent text-zinc-500">
                                            BY SUBMITTING YOU AGREE WE MAY CONTACT YOU ABOUT WE DELVR LOGISTICS.
                                        </p>
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            className="btn-primary disabled:opacity-60"
                                            data-testid="ea-submit-btn"
                                        >
                                            {submitting ? "Submitting..." : "Join Early Access"}
                                            <ArrowRight size={18} />
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Field({ label, testid, full, children }) {
    return (
        <label className={`block ${full ? "sm:col-span-2" : ""}`} data-testid={testid}>
            <span className="block text-xs font-mono-accent text-zinc-400 mb-2 uppercase tracking-[0.18em]">
                {label}
            </span>
            {children}
        </label>
    );
}
